import React, { useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { ChevronRight, Filter } from 'lucide-react';
import Zoom from '@/assets/icons/zoom';
import { ChatsAndCallsAgent } from '@/modules/lead-page/api/lead-page.v2.api';
import { ActivityItem, WhatsappConversationListItem } from '@/modules/lead-page/dtos/index-v2';
import { useWhatsappConversations } from '@/modules/lead-page/new/hooks/whatsapp-conversations.hook';
import { useZoomCallsActivity } from '@/modules/lead-page/new/hooks/zoom-calls-activity.hook';
import { SelectedItem } from '@/modules/lead-page/new/components/chats-and-calls/chats-and-calls-tab';
import AgentFilterPopover from '@/modules/lead-page/new/components/chats-and-calls/agent-filter-popover';
import CommonAvatar from '@/modules/common/components/ui-kit/common-avatar';
import SquareButton from '@/modules/common/components/ui/square-button';

interface Props {
	journeyUid: string;
	selected: SelectedItem | null;
	onSelectWhatsapp: (agentUid: string, agentName: string | null, conversations: WhatsappConversationListItem[]) => void;
	onSelectZoom: (zoomCallUid: string, activityItem: ActivityItem) => void;
	fullWidth?: boolean;
	agents: ChatsAndCallsAgent[];
	selectedAgents: Set<string>;
	onToggleAgent: (agentUid: string) => void;
	/** Tab-level decision from the initial unfiltered fetch — hidden when it
	    came back empty or failed (e.g. 401). null while still undecided. */
	showWhatsappSection: boolean | null;
	showCallsSection: boolean | null;
}

const formatDate = (dateStr: string): string => {
	try {
		return format(parseISO(dateStr), 'd MMM yyyy, h:mm a');
	} catch {
		return dateStr;
	}
};

const ChatsAndCallsLeftPanel: React.FC<Props> = ({
	journeyUid,
	selected,
	onSelectWhatsapp,
	onSelectZoom,
	fullWidth = false,
	agents,
	selectedAgents,
	onToggleAgent,
	showWhatsappSection,
	showCallsSection,
}) => {
	const theme = useTheme();
	const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);

	// Filtering is server-side: the selected agent uids go into both list queries.
	const agentUids = useMemo(() => Array.from(selectedAgents), [selectedAgents]);

	const {
		agentGroups,
		isLoading: whatsappLoading,
		fetchNextPage: fetchNextWhatsappPage,
		hasNextPage: hasNextWhatsappPage,
		isFetchingNextPage: isFetchingNextWhatsappPage,
	} = useWhatsappConversations(journeyUid, { agentUids });

	const {
		zoomItems,
		isLoading: zoomLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useZoomCallsActivity(journeyUid, { agentUids });

	/* Until both initial calls settle, the list shows one bare loader — no
	   section headers; afterwards the tab-level decisions alone control the
	   sections. */
	const sectionsPending = showWhatsappSection === null || showCallsSection === null;
	const showWhatsapp = showWhatsappSection === true;
	const showCalls = showCallsSection === true;

	const listContainerRef = useRef<HTMLDivElement | null>(null);
	const whatsappLoadMoreRef = useRef<HTMLDivElement | null>(null);
	const loadMoreRef = useRef<HTMLDivElement | null>(null);

	/* Each section pages when its end marker reaches the bottom of the panel, and
	   only in response to a scroll gesture. An IntersectionObserver can't be used
	   here: while a section is shorter than the panel its marker is on screen from
	   the first render, so it would chain page after page with no user input. Wheel
	   and touch are handled alongside scroll so a section that doesn't yet overflow
	   the panel — nothing to scroll — still pages on the gesture. */
	const pageOnScrollGesture = (): void => {
		const root = listContainerRef.current;
		if (!root) return;
		const triggerLine = root.getBoundingClientRect().bottom + 120;
		const reached = (marker: HTMLDivElement | null): boolean =>
			!!marker && marker.getBoundingClientRect().top <= triggerLine;

		if (reached(whatsappLoadMoreRef.current) && hasNextWhatsappPage && !isFetchingNextWhatsappPage) {
			fetchNextWhatsappPage();
		}
		if (reached(loadMoreRef.current) && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	};

	const sectionHeaderSx = {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		px: theme.nawy.spacing.sm,
		pt: theme.nawy.spacing.sm,
		pb: theme.nawy.spacing.xs,
	};

	const sectionLabelSx = {
		...theme.nawy.typography.english.variants.body.sm.regular,
		color: theme.nawy.colors.semantic.text.tertiary,
	};

	const cardBaseSx = {
		display: 'flex',
		alignItems: 'flex-start',
		gap: '12px',
		px: '12px',
		py: '12px',
		cursor: 'pointer',
		borderRadius: '12px',
		mx: theme.nawy.spacing.sm,
		mb: theme.nawy.spacing.xs,
		border: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
		backgroundColor: theme.nawy.colors.semantic.layer.primary,
		boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
		transition: 'background-color 0.15s, border-color 0.15s, box-shadow 0.15s',
		'&:hover': {
			backgroundColor: theme.nawy.colors.semantic.layer.tertiary,
		},
	};

	const selectedCardSx = {
		backgroundColor: theme.nawy.colors.semantic.layer.tertiary,
		'&:hover': {
			backgroundColor: theme.nawy.colors.semantic.layer.tertiary,
		},
	};

	const loadMoreRowSx = {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: 24,
		py: theme.nawy.spacing.xs,
	};

	return (
		<Box
			sx={{
				width: fullWidth ? '100%' : '360px',
				flexShrink: 0,
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				borderRight: fullWidth
					? 'none'
					: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
				overflow: 'hidden',
				backgroundColor: theme.nawy.colors.semantic.layer.primary,
			}}
		>
			{/* Scrollable list */}
			<Box
				ref={listContainerRef}
				onScroll={pageOnScrollGesture}
				onWheel={pageOnScrollGesture}
				onTouchMove={pageOnScrollGesture}
				sx={{ flex: 1, overflowY: 'auto', py: theme.nawy.spacing.xs }}
			>
				{sectionsPending ? (
					/* Initial load — one bare loader, no section headers yet */
					<Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<CircularProgress size={24} />
					</Box>
				) : !showWhatsapp && !showCalls ? (
					<Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<Typography sx={sectionLabelSx}>No chats or calls</Typography>
					</Box>
				) : (
					<>
				{/* WhatsApp section */}
				{showWhatsapp && (
					<Box sx={{ mb: theme.nawy.spacing.sm }}>
						{/* Section header with filter */}
						<Box sx={{ ...sectionHeaderSx, ...(fullWidth ? { justifyContent: 'flex-start' } : {}) }}>
							<Typography sx={sectionLabelSx}>Whatsapp chat</Typography>
							{!fullWidth && (
								<Box sx={{ position: 'relative' }}>
									<SquareButton
										variant='ghost'
										onClick={(e) => setFilterAnchor(e.currentTarget as HTMLElement)}
										title='Filter by agent'
									>
										<Filter size={16} />
									</SquareButton>
									{selectedAgents.size > 0 && (
										<Box
											sx={{
												position: 'absolute',
												top: 4,
												right: 4,
												width: 8,
												height: 8,
												borderRadius: '50%',
												backgroundColor: theme.nawy.colors.semantic.indicator.blue,
												pointerEvents: 'none',
											}}
										/>
									)}
								</Box>
							)}
						</Box>

						{whatsappLoading ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', py: theme.nawy.spacing.sm }}>
								<CircularProgress size={20} />
							</Box>
						) : agentGroups.length === 0 ? (
							/* Only reachable via the agent filter — an empty initial load
							   hides the whole section instead. */
							<Typography sx={{ ...sectionLabelSx, px: theme.nawy.spacing.sm, py: theme.nawy.spacing.xs }}>
								No conversations for the selected agents
							</Typography>
						) : (
							agentGroups.map((group) => {
								const isSelected =
									selected?.kind === 'whatsapp' && selected.agentUid === group.agentUid;
								return (
									<Box
										key={group.agentUid}
										sx={{
											...cardBaseSx,
											...(isSelected ? selectedCardSx : {}),
										}}
										onClick={() => {
											if (!group.agentUid) return;
											onSelectWhatsapp(group.agentUid, group.agentName, group.conversations);
										}}
									>
										<CommonAvatar name={group.agentName ?? undefined} size={40} />
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: '2px' }}>
												<Typography
													noWrap
													sx={{
														...theme.nawy.typography.english.variants.body.md.semibold,
														color: theme.nawy.colors.semantic.text.primary,
														flex: 1,
														minWidth: 0,
													}}
												>
													{group.agentName ?? group.agentUid}
												</Typography>
												<Typography
													sx={{
														...theme.nawy.typography.english.variants.body.sm.regular,
														color: theme.nawy.colors.semantic.text.tertiary,
														flexShrink: 0,
														whiteSpace: 'nowrap',
													}}
												>
													{formatDate(group.lastMessageAt)}
												</Typography>
											</Box>
										</Box>
										<Box sx={{ flexShrink: 0, alignSelf: 'center', ml: '2px' }}>
											<ChevronRight size={16} color={theme.nawy.colors.semantic.text.tertiary} />
										</Box>
									</Box>
								);
							})
						)}
						{!whatsappLoading && hasNextWhatsappPage && (
							<Box ref={whatsappLoadMoreRef} sx={loadMoreRowSx}>
								{isFetchingNextWhatsappPage && <CircularProgress size={16} />}
							</Box>
						)}
					</Box>
				)}

				{/* Calls section */}
				{showCalls && (
					<Box>
						<Box sx={{ ...sectionHeaderSx, justifyContent: 'flex-start' }}>
							<Typography sx={sectionLabelSx}>Calls</Typography>
						</Box>
						{zoomLoading ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', py: theme.nawy.spacing.sm }}>
								<CircularProgress size={20} />
							</Box>
						) : zoomItems.length === 0 ? (
							<Typography sx={{ ...sectionLabelSx, px: theme.nawy.spacing.sm, py: theme.nawy.spacing.xs }}>
								No calls for the selected agents
							</Typography>
						) : (
							zoomItems.map((item) => {
								const isSelected =
									selected?.kind === 'zoom' && selected.zoomCallUid === item.zoomCallUid;
								const summaryPoints = (item.zoomCallSummary ?? []).filter(
									(s): s is string => typeof s === 'string' && s.trim().length > 0,
								);
								const summaryPreview =
									summaryPoints.length > 0 ? summaryPoints.join('. ') : null;
								return (
									<Box
										key={item.uid}
										sx={{
											...cardBaseSx,
											alignItems: 'center',
											...(isSelected ? selectedCardSx : {}),
										}}
										onClick={() => {
											if (item.zoomCallUid) {
												onSelectZoom(item.zoomCallUid, item);
											}
										}}
									>
										<Box
											sx={{
												width: 32,
												height: 32,
												borderRadius: '50%',
												border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
												backgroundColor: theme.nawy.colors.semantic.layer.primary,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												flexShrink: 0,
											}}
										>
											<Zoom />
										</Box>
										<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
												<CommonAvatar name={item.actorName ?? undefined} size={20} />
												<Typography
													noWrap
													sx={{
														...theme.nawy.typography.english.variants.body.md.semibold,
														color: theme.nawy.colors.semantic.text.primary,
														minWidth: 0,
														flex: 1,
													}}
												>
													{item.actorName ?? 'Zoom call'}
												</Typography>
											</Box>
											{summaryPreview && (
												<Typography
													sx={{
														...theme.nawy.typography.english.variants.body.sm.regular,
														color: theme.nawy.colors.semantic.text.secondary,
														display: '-webkit-box',
														WebkitLineClamp: 2,
														WebkitBoxOrient: 'vertical',
														overflow: 'hidden',
													}}
												>
													Call summary: {summaryPreview}
												</Typography>
											)}
											<Typography
												sx={{
													...theme.nawy.typography.english.variants.body.sm.regular,
													color: theme.nawy.colors.semantic.text.tertiary,
												}}
											>
												{formatDate(item.createdAt)}
											</Typography>
										</Box>
										<Box sx={{ flexShrink: 0, alignSelf: 'center', ml: '2px' }}>
											<ChevronRight size={16} color={theme.nawy.colors.semantic.text.tertiary} />
										</Box>
									</Box>
								);
							})
						)}
						{!zoomLoading && hasNextPage && (
							<Box ref={loadMoreRef} sx={loadMoreRowSx}>
								{isFetchingNextPage && <CircularProgress size={16} />}
							</Box>
						)}
					</Box>
				)}
					</>
				)}
			</Box>

			{/* Agent filter popover */}
			<AgentFilterPopover
				anchorEl={filterAnchor}
				onClose={() => setFilterAnchor(null)}
				agents={agents}
				selectedAgents={selectedAgents}
				onToggleAgent={onToggleAgent}
			/>
		</Box>
	);
};

export default ChatsAndCallsLeftPanel;
